import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchDetectionRules, createDetectionRule, publishDetectionRule, disableDetectionRule, testDetectionRule,
  fetchSuppressions, createSuppression, runThreatHunt, simulateSecurityEvent,
} from '../../services/api/detectionEngineeringService';

export const loadDetectionEngineering = createAsyncThunk('detectionEngineering/load', async () => {
  const [rules, suppressions] = await Promise.all([fetchDetectionRules(), fetchSuppressions()]);
  return { rules, suppressions };
});

export const addRule = createAsyncThunk('detectionEngineering/addRule', async (payload) => {
  await createDetectionRule(payload);
  return fetchDetectionRules();
});

export const publishRule = createAsyncThunk('detectionEngineering/publishRule', async (id) => {
  await publishDetectionRule(id);
  return fetchDetectionRules();
});

export const disableRule = createAsyncThunk('detectionEngineering/disableRule', async (id) => {
  await disableDetectionRule(id);
  return fetchDetectionRules();
});

export const testRule = createAsyncThunk('detectionEngineering/testRule', async ({ id, payload }) => {
  const result = await testDetectionRule(id, payload);
  return result;
});

export const addSuppression = createAsyncThunk('detectionEngineering/addSuppression', async (payload) => {
  await createSuppression(payload);
  return fetchSuppressions();
});

export const runHunt = createAsyncThunk('detectionEngineering/runHunt', async (payload) => {
  return runThreatHunt(payload);
});

export const simulateEvent = createAsyncThunk('detectionEngineering/simulateEvent', async (payload) => simulateSecurityEvent(payload));

const detectionEngineeringSlice = createSlice({
  name: 'detectionEngineering',
  initialState: { status: 'idle', error: null, rules: [], suppressions: [], huntResults: [], huntRanAt: null, lastTestResult: null, lastSimulation: null },
  reducers: {
    clearHuntResults(state) { state.huntResults = []; state.huntRanAt = null; },
    clearTestResult(state) { state.lastTestResult = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadDetectionEngineering.pending, (state) => { state.status = 'loading'; })
      .addCase(loadDetectionEngineering.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.rules = action.payload.rules;
        state.suppressions = action.payload.suppressions;
      })
      .addCase(loadDetectionEngineering.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(addRule.fulfilled, (state, action) => { state.rules = action.payload; })
      .addCase(publishRule.fulfilled, (state, action) => { state.rules = action.payload; })
      .addCase(disableRule.fulfilled, (state, action) => { state.rules = action.payload; })
      .addCase(testRule.fulfilled, (state, action) => { state.lastTestResult = action.payload; })
      .addCase(addSuppression.fulfilled, (state, action) => { state.suppressions = action.payload; })
      .addCase(runHunt.fulfilled, (state, action) => {
        state.huntResults = Array.isArray(action.payload) ? action.payload : [];
        state.huntRanAt = Date.now();
      })
      .addCase(simulateEvent.fulfilled, (state, action) => { state.lastSimulation = action.payload; });
  }
});

export const { clearHuntResults, clearTestResult } = detectionEngineeringSlice.actions;
export default detectionEngineeringSlice.reducer;
